import { useCallback, useEffect, useState } from "react";
import {
  listDocumentTypesAction,
  getDocumentTypeByIdAction,
  deleteDocumentTypeAction,
  updateDocumentTypeStatusAction
} from "../../Actions/DocumentTypeActions";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import CustomSnackbar from "../Common/snackBar";
import AddEditDocumentType from "./AddEditDocumentType";
import { renderStatusBadge } from "../../utils/badges";
import DataTable from "../../components/DataTable/DataTable";

const DocumentTypeList = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRowsPerPage, setSelectedRowsPerPage] =
    useState(ITEMS_PER_PAGE_OPTIONS[0]);

  const [filterValues, setFilterValues] = useState({
    search: "",
    status: "all",
  });

  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const res = await listDocumentTypesAction(
        currentPage,
        selectedRowsPerPage,
        filterValues.search,
        "",
        "",
        filterValues.status
      );

      setData(res?.data?.data || []);
      setTotalRecords(res?.data?.meta?.total || 0);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Error fetching data",
        severity: "error",
      });
    }

    setLoading(false);
  }, [currentPage, selectedRowsPerPage, filterValues.search, filterValues.status,]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterApply = (values) => {
    setFilterValues(values);
    setCurrentPage(1);
  };

  const handleFilterReset = () => {
    setFilterValues({ search: "", status: "all", });
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setSelectedRowsPerPage(size);
    setCurrentPage(1);
  };

  const handleEdit = (id) => {
    getDocumentTypeByIdAction(id)
      .then((res) => {
        setEditData(res?.data);
        setOpenModal(true);
      })
      .catch((error) => {
        console.error("Error fetching document type:", error);
      });
  };

  const handleDelete = async (id, row) => {
    try {

      const payload = {
        changedFields: JSON.stringify(["status"]),
        comparisonData: JSON.stringify({
          document_type: {
            status: {
              old: "Active",
              new: "Deleted",
            },
          },
        }),
      };

      const res = await deleteDocumentTypeAction(id, payload);

      setSnackbar({
        open: true,
        message: res?.data?.message || "Deleted successfully",
        severity: "success",
      });

      fetchData();

    } catch (error) {

      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Something went wrong",
        severity: "error",
      });
    }
  };

  const handleStatusToggle = async (row) => {
    try {

      const payload = {
        status: !row.status,
        changedFields: JSON.stringify(["status"]),
        comparisonData: JSON.stringify({
          document_type: {
            status: {
              old: row.status,
              new: !row.status,
            },
          },
        }),
      };

      await updateDocumentTypeStatusAction(
        row.id,
        payload
      );

      setSnackbar({
        open: true,
        message: `Document type ${!row.status ? "enabled" : "disabled"
          } successfully`,
        severity: "success",
      });

      fetchData();

    } catch (error) {

      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Something went wrong",
        severity: "error",
      });
    }
  };

  const handleAction = (action, row) => {
    if (action === "edit") handleEdit(row.id);
    if (action === "delete") handleDelete(row.id, row);
    if (action === "activate" || action === "deactivate") {
      handleStatusToggle(row);
    }
  };

  const columns = [
    { field: "code", headerName: "Code" },
    { field: "name", headerName: "Name" },
    { field: "provider", headerName: "Provider" },
    { field: "provider_code", headerName: "Provider Code" },
    {
      field: "status",
      headerName: "Status",
      renderCell: ({ row }) =>
        renderStatusBadge({
          status: row.status ? "success" : "default",
          title: row.status ? "Active" : "Inactive",
        }),
    },
  ];

  return (
    <div>
      <BreadcrumbsComponent />

      <DataTable
        rows={data}
        columns={columns}
        statusField="status"
        loading={loading}
        totalCount={totalRecords}
        page={currentPage}
        pageSize={selectedRowsPerPage}
        getRowId={(row) => row.id}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onFilterApply={handleFilterApply}
        onFilterReset={handleFilterReset}
        filterValues={filterValues}
        filters={[
          {
            type: "search",
            placeholder: "Search document types...",
          },
          {
            type: "select",
            name: "status",
            label: "Status",
            options: [
              { label: "All", value: "all" },
              { label: "Active", value: "true" },
              { label: "Inactive", value: "false" },
            ]
          },
        ]}
        actions={{
          edit: true,
          delete: true,
          activate: true,
          deactivate: true,
        }}
        onAction={handleAction}
        onAddClick={() => {
          setEditData(null);
          setOpenModal(true);
        }}
        hasCreatePermission={true}
        addButtonText="ADD"
      />

      {openModal && (
        <AddEditDocumentType
          open={openModal}
          handleClose={() => {
            setOpenModal(false);
            setEditData(null);
          }}
          editData={editData}
          setSnackbar={setSnackbar}
          refresh={fetchData}
        />
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
};

export default DocumentTypeList;